import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";
import {
  Layers,
  Boxes,
  QrCode,
  ShieldCheck,
  TrendingDown,
  Building2,
  Leaf,
  Wifi,
  Lock,
  Car,
  Sparkles,
  Sun,
  ArrowRight,
} from "lucide-react";

const PHASES_BY_LANG = {
  de: [
    { week: "1–4", title: "BIM-Koordination", text: "Arch / Str / TGA, Freigaben, Werksplanung." },
    { week: "5–10", title: "Vorfertigung", text: "Wände, Decken, Schächte, Fassade. FAT/Qualitätsabnahme im Werk." },
    { week: "7–8", title: "Baustelle vorbereiten", text: "Baustelleneinrichtung, Fundament, Anschlussdetails." },
    { week: "9–16", title: "Montage Rohbau", text: "Im 9-m-Takt, Technikmodule, QR-gestützte Abnahmen pro Geschoss." },
    { week: "17–22", title: "Ausbau & Inbetriebnahme", text: "Test & Balance, Brandschutz, digitales Prüfbuch." },
    { week: "23", title: "Übergabe", text: "Inkl. digitalem Zwilling, As-Built-BIM, Wartungs- und Ersatzteildaten." },
  ],
  en: [
    { week: "1–4", title: "BIM coordination", text: "Architecture / structure / MEP, approvals, shop drawings." },
    { week: "5–10", title: "Prefabrication", text: "Walls, slabs, shafts, façade. FAT / QA at the factory." },
    { week: "7–8", title: "Site preparation", text: "Site setup, foundations, connection details." },
    { week: "9–16", title: "Structural assembly", text: "9-m takt, mechanical modules, QR-based per-floor sign-off." },
    { week: "17–22", title: "Fit-out & commissioning", text: "Test & balance, fire safety, digital inspection log." },
    { week: "23", title: "Handover", text: "Including digital twin, as-built BIM, maintenance & spares data." },
  ],
  ru: [
    { week: "1–4", title: "BIM-координация", text: "Архитектура / конструкции / ИОС, согласования, рабочие чертежи." },
    { week: "5–10", title: "Префабрикация", text: "Стены, плиты, шахты, фасад. FAT/контроль качества на заводе." },
    { week: "7–8", title: "Подготовка площадки", text: "Размещение, фундамент, узлы соединений." },
    { week: "9–16", title: "Монтаж конструкций", text: "Тактом 9 м, инженерные модули, QR-приёмка по этажам." },
    { week: "17–22", title: "Отделка и пуск", text: "Test & balance, противопожарная защита, цифровой журнал." },
    { week: "23", title: "Сдача", text: "Цифровой двойник, as-built BIM, сервис и запчасти." },
  ],
};

const E2E_FEATURES_BY_LANG = {
  de: [
    { icon: Layers, no: "01", title: "Durchgängige BIM-Planung", points: ["Architektur, Tragwerk, TGA & Ausbau koordiniert (LOD 300–400)", "Kollisionsprüfung und Mengen direkt aus dem Modell", "Fertigungs- und Montagepläne automatisiert ausgeleitet"] },
    { icon: Boxes, no: "02", title: "Vorfertigung in Serie", points: ["Wände, Decken, Schächte, Fassaden präzisionsgefertigt", "Werkseitige Integration von Elektro, HVAC, Befestigung & Dichtung", "Toleranz- und Qualitätsprüfung im Werk"] },
    { icon: QrCode, no: "03", title: "QR-Code je Bauteil", points: ["Live-Zugriff auf Spezifikation, Montagefolge & Prüfprotokolle", "Digitale Mängelverfolgung", "Wartungshinweise auf einen Scan abrufbar"] },
    { icon: ShieldCheck, no: "04", title: "Seismisch robustes 9-m-Raster", points: ["Wirtschaftliches 9,0-m-Stützenraster", "Flexible Grundrisse, schnelle Leitungsführung", "Eurocode-Auslegung mit duktilen Details"] },
    { icon: TrendingDown, no: "05", title: "Nachweisbare Effekte", points: ["≈ +40 % schneller in der Gesamtbauzeit", "–40 bis –50 % Material & Arbeitsstunden", "–25 % Logistikaufwand durch JIS-Anlieferung"] },
    { icon: Building2, no: "06", title: "Doku & digitaler Zwilling", points: ["Werkfertigung, QR-Scans, Fassadenmontage im Takt", "Taktplan, Beispiel-QR-Datensatz, ITP-Protokolle", "Vor-/Nach-Vergleich Bauzeit & Kosten"] },
  ],
  en: [
    { icon: Layers, no: "01", title: "End-to-end BIM planning", points: ["Architecture, structure, MEP & finishes coordinated (LOD 300–400)", "Clash detection and quantities directly from the model", "Fabrication and assembly drawings auto-generated"] },
    { icon: Boxes, no: "02", title: "Serial prefabrication", points: ["Walls, slabs, shafts and façades precision-manufactured", "Factory-integrated electrical, HVAC, fixings & sealing", "Tolerance and QC inspection in the factory"] },
    { icon: QrCode, no: "03", title: "QR per component", points: ["Live access to specs, sequence & inspection records", "Digital defect tracking", "Maintenance manuals available with one scan"] },
    { icon: ShieldCheck, no: "04", title: "Seismic 9-m grid", points: ["Economical 9.0-m structural grid", "Flexible plans, fast service routing", "Eurocode design with ductile detailing"] },
    { icon: TrendingDown, no: "05", title: "Verifiable effects", points: ["≈ +40 % faster overall construction time", "–40 to –50 % material & labour", "–25 % logistics through just-in-sequence delivery"] },
    { icon: Building2, no: "06", title: "Docs & digital twin", points: ["Factory fabrication, QR scans, façade assembly in takt", "Takt schedule, sample QR record, ITP protocols", "Before/after comparison of time & cost"] },
  ],
  ru: [
    { icon: Layers, no: "01", title: "Сквозное BIM-проектирование", points: ["Архитектура, конструкции, ИОС и отделка в LOD 300–400", "Поиск коллизий и спецификации прямо из модели", "Чертежи изготовления и монтажа — автоматически"] },
    { icon: Boxes, no: "02", title: "Серийная префабрикация", points: ["Стены, плиты, шахты, фасады точного изготовления", "Заводская интеграция электрики, HVAC, крепежа", "Контроль качества и допусков на заводе"] },
    { icon: QrCode, no: "03", title: "QR на каждой детали", points: ["Доступ к спецификации и протоколам онлайн", "Цифровое отслеживание дефектов", "Инструкции по обслуживанию по сканированию"] },
    { icon: ShieldCheck, no: "04", title: "Сейсмическая сетка 9 м", points: ["Экономичная сетка колонн 9 м", "Гибкие планировки, быстрые коммуникации", "Расчёт по Еврокоду, пластичная арматура"] },
    { icon: TrendingDown, no: "05", title: "Подтверждённый эффект", points: ["≈ +40 % быстрее по общему сроку", "–40…50 % материала и трудозатрат", "–25 % логистики за счёт JIS-доставки"] },
    { icon: Building2, no: "06", title: "Документация и цифр. двойник", points: ["Заводское изготовление, QR-сканы, монтаж тактом", "Тактовый план, пример QR, ITP-протоколы", "Сравнение до/после: сроки и стоимость"] },
  ],
};

const EQUIPMENT_BY_LANG = {
  de: [
    { icon: Sparkles, title: "Komfort", items: ["Helle, offene Wohnbereiche", "Bodentiefe Fenster", "Balkone, Loggien & teils Eigengärten", "Aufzug, barrierearme Erschließung"] },
    { icon: Building2, title: "Ausstattung", items: ["Premium-Küchen mit Markengeräten", "Designbäder mit Qualitätsarmaturen", "Edle Bodenbeläge", "Fußbodenheizung/-kühlung (projektabhängig)"] },
    { icon: Wifi, title: "Technik", items: ["Smart-Home (Licht, Klima, Zutritt)", "Glasfaser & High-Speed-Internet", "Multimedia-Verkabelung", "Effiziente, leise Haustechnik"] },
    { icon: Leaf, title: "Nachhaltigkeit", items: ["Energieeffiziente Bauweise", "Hochwertige Dämmung", "PV-Unterstützung möglich", "Wasser- & Abfallmanagement"] },
    { icon: Lock, title: "Sicherheit", items: ["Video-Gegensprechanlage", "Gesicherter Zugang", "Fahrrad- & Kinderwagenraum", "Paketbereich"] },
    { icon: Car, title: "Mobilität & Service", items: ["Tiefgarage mit E-Ladeoption", "Gute Anbindung an Öffis", "Hausverwaltung", "Optional Concierge-Service"] },
  ],
  en: [
    { icon: Sparkles, title: "Comfort", items: ["Bright, open living spaces", "Floor-to-ceiling windows", "Balconies, loggias & some private gardens", "Lift and barrier-free access"] },
    { icon: Building2, title: "Fittings", items: ["Premium kitchens with brand appliances", "Designer bathrooms with quality fittings", "Refined flooring", "Underfloor heating/cooling (project dependent)"] },
    { icon: Wifi, title: "Technology", items: ["Smart home (light, climate, access)", "Fibre & high-speed internet", "Multimedia wiring", "Efficient, quiet building services"] },
    { icon: Leaf, title: "Sustainability", items: ["Energy-efficient construction", "High-grade insulation", "PV-ready", "Water & waste management"] },
    { icon: Lock, title: "Security", items: ["Video intercom", "Secure access", "Bicycle & stroller room", "Package area"] },
    { icon: Car, title: "Mobility & service", items: ["Underground parking with EV charging", "Excellent public transport links", "Property management", "Optional concierge service"] },
  ],
  ru: [
    { icon: Sparkles, title: "Комфорт", items: ["Светлые, открытые жилые зоны", "Окна в пол", "Балконы, лоджии, частично сады", "Лифт и безбарьерный доступ"] },
    { icon: Building2, title: "Оснащение", items: ["Премиум-кухни с брендовой техникой", "Дизайнерские ванные", "Качественные напольные покрытия", "Тёплый пол / охлаждение (по проекту)"] },
    { icon: Wifi, title: "Технологии", items: ["Умный дом (свет, климат, доступ)", "Оптоволокно и быстрый интернет", "Мультимедиа-проводка", "Эффективные и тихие инженерные системы"] },
    { icon: Leaf, title: "Экология", items: ["Энергоэффективное строительство", "Качественное утепление", "Поддержка PV", "Управление водой и отходами"] },
    { icon: Lock, title: "Безопасность", items: ["Видеодомофон", "Защищённый доступ", "Велосипедная и колясочная", "Зона для посылок"] },
    { icon: Car, title: "Мобильность", items: ["Подземный паркинг с зарядкой EV", "Хорошая транспортная связь", "Управляющая компания", "Консьерж-сервис (опция)"] },
  ],
};

const APARTMENTS_BY_LANG = {
  de: [
    { type: "1-Zimmer", title: "Kompakt & smart", desc: "Wohlfühlräume mit Funktion. Warme Materialien, gutes Licht, klare Linien.", image: "/images/leistung-1zimmer.jpg" },
    { type: "2-Zimmer", title: "Optimal für Paare", desc: "Durchdachte Grundrisse, viel Stauraum, helle Räume — alltagstauglich mit Wohlfühlfaktor.", image: "/images/leistung-2zimmer.jpg" },
    { type: "3-Zimmer", title: "Großzügig & luxuriös", desc: "Weite Grundrisse, viel Licht, edle Materialien. Premium-Wohngefühl in jedem Raum.", image: "/images/leistung-3zimmer.jpg" },
    { type: "Reihenhaus", title: "Exklusives Wohnen", desc: "Klare Architektur, hochwertige Materialien, eigener Garten — Komfort auf höchstem Niveau.", image: "/images/leistung-reihenhaus.jpg" },
  ],
  en: [
    { type: "1-Room", title: "Compact & smart", desc: "Functional comfort. Warm materials, great light, clean lines.", image: "/images/leistung-1zimmer.jpg" },
    { type: "2-Room", title: "Ideal for couples", desc: "Considered floor plans, plenty of storage, bright rooms — practical and welcoming.", image: "/images/leistung-2zimmer.jpg" },
    { type: "3-Room", title: "Generous & luxurious", desc: "Open plans, plenty of light, refined materials. Premium living throughout.", image: "/images/leistung-3zimmer.jpg" },
    { type: "Townhouse", title: "Exclusive living", desc: "Clean architecture, high-grade materials, private garden — comfort at the highest level.", image: "/images/leistung-reihenhaus.jpg" },
  ],
  ru: [
    { type: "1-комнатные", title: "Компактно и умно", desc: "Уютные функциональные пространства. Тёплые материалы, свет, чёткие линии.", image: "/images/leistung-1zimmer.jpg" },
    { type: "2-комнатные", title: "Идеально для пары", desc: "Продуманные планировки, много мест для хранения, светлые комнаты.", image: "/images/leistung-2zimmer.jpg" },
    { type: "3-комнатные", title: "Просторно и роскошно", desc: "Открытые планировки, много света, премиум-материалы.", image: "/images/leistung-3zimmer.jpg" },
    { type: "Таунхаус", title: "Эксклюзивное жильё", desc: "Чёткая архитектура, качественные материалы, собственный сад.", image: "/images/leistung-reihenhaus.jpg" },
  ],
};

const PHASES = PHASES_BY_LANG.de;
const E2E_FEATURES = E2E_FEATURES_BY_LANG.de;
const EQUIPMENT = EQUIPMENT_BY_LANG.de;
const APARTMENTS = APARTMENTS_BY_LANG.de;

export default function LeistungenPage() {
  const { t, lang } = useLang();
  const phases = PHASES_BY_LANG[lang] || PHASES_BY_LANG.ru || PHASES_BY_LANG.de;
  const features = E2E_FEATURES_BY_LANG[lang] || E2E_FEATURES_BY_LANG.ru || E2E_FEATURES_BY_LANG.de;
  const equipment = EQUIPMENT_BY_LANG[lang] || EQUIPMENT_BY_LANG.ru || EQUIPMENT_BY_LANG.de;
  const apartments = APARTMENTS_BY_LANG[lang] || APARTMENTS_BY_LANG.ru || APARTMENTS_BY_LANG.de;
  return (
    <div data-testid="leistungen-page" className="pt-32">
      <PageMeta pageKey="services" />
      {/* Header */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 pb-16">
        <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("services.eyebrow")}</p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-light uppercase tracking-tighter leading-[0.95]">
          {t("services.title.l1")} <br />
          <span className="italic">{t("services.title.l2")}</span>
        </h1>
        <p className="mt-10 max-w-3xl text-lg md:text-xl text-white/70 font-light leading-relaxed">
          {t("services.lede")}
        </p>
      </section>

      {/* E2E Bento */}
      <section className="bg-[#0A2244]/50 border-y border-white/10 py-20 md:py-28">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
            {features.map((f) => (
              <div
                key={f.no}
                className="border-b border-r border-white/10 p-8 md:p-10"
                data-testid={`leistung-feature-${f.no}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="font-display text-3xl text-white/30">{f.no}</span>
                  <f.icon size={28} strokeWidth={1.25} />
                </div>
                <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight mb-5">
                  {f.title}
                </h3>
                <ul className="space-y-2.5">
                  {f.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-3 text-sm text-white/65 leading-relaxed">
                      <span className="mt-2 w-3 h-px bg-white/40 shrink-0" />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 md:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("services.timeline.eyebrow")}</p>
              <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05]">
                {t("services.timeline.title.l1")} <br />{t("services.timeline.title.l2")} <br />{t("services.timeline.title.l3")}
              </h2>
            </div>
            <div className="md:col-span-7 md:pt-8">
              <p className="text-base md:text-lg text-white/70 font-light leading-relaxed">
                {t("services.timeline.lede")}
              </p>
            </div>
          </div>

          <div className="border-t border-white/10">
            {phases.map((p, i) => (
              <div
                key={p.week}
                className="grid grid-cols-12 gap-6 md:gap-10 border-b border-white/10 py-8 md:py-10 group hover:bg-white/[0.02] transition-colors"
                data-testid={`phase-${i}`}
              >
                <div className="col-span-2 md:col-span-1 font-display text-xs tracking-[0.2em] text-white/50 pt-2">
                  W {p.week}
                </div>
                <div className="col-span-10 md:col-span-4">
                  <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight">{p.title}</h3>
                </div>
                <div className="col-span-12 md:col-span-7 text-white/65 text-sm md:text-base leading-relaxed">
                  {p.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Apartments */}
      <section className="bg-[#0A2244]/50 border-y border-white/10 py-24 md:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("services.apartments.eyebrow")}</p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16 max-w-3xl">
            {t("services.apartments.title.l1")} <br />
            {t("services.apartments.title.l2")} <br />{t("services.apartments.title.l3")}
          </h2>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {apartments.map((a, idx) => (
              <div
                key={a.type}
                className="border border-white/10 hover:border-white/40 transition-colors group overflow-hidden"
                data-testid={`apartment-${idx}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={a.image}
                    alt={a.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-8">
                  <p className="text-[11px] tracking-[0.25em] uppercase text-white/50 mb-3">{a.type}</p>
                  <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight">{a.title}</h3>
                  <p className="mt-3 text-sm text-white/65 leading-relaxed">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment */}
      <section className="py-24 md:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("services.equipment.eyebrow")}</p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16 max-w-3xl">
            {t("services.equipment.title.l1")} <br />{t("services.equipment.title.l2")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
            {equipment.map((g) => (
              <div key={g.title} className="border-b border-r border-white/10 p-8 md:p-10">
                <div className="flex items-center gap-4 mb-6">
                  <g.icon size={26} strokeWidth={1.25} />
                  <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight">{g.title}</h3>
                </div>
                <ul className="space-y-2.5">
                  {g.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-sm text-white/65 leading-relaxed">
                      <Sun size={12} strokeWidth={1.25} className="mt-1 shrink-0 text-white/40" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-16 flex flex-col sm:flex-row gap-4">
            <Link
              to="/projekte"
              className="rh-btn-primary inline-flex items-center justify-center gap-3 bg-white text-[#051630] font-bold tracking-[0.18em] uppercase text-xs px-8 py-5 border border-white"
              data-testid="leistungen-cta-projekte"
            >
              <span>{t("services.cta_projects")}</span>
              <ArrowRight size={16} strokeWidth={2} />
            </Link>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-3 bg-transparent text-white font-medium tracking-[0.18em] uppercase text-xs px-8 py-5 border border-white/30 hover:border-white transition-colors"
              data-testid="leistungen-cta-kontakt"
            >
              {t("services.cta_contact")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
